//================================================================
//  Component: QuoteDetail
//================================================================

//  Purpose: This is a container summarising the details of a single quote

//  Properties:
//    - index = {number, used to store the index of the current quote}
//    - quote = {object, stores the quote details}
//    - quotePaneData = {useReducer, used store the quote pane data}
//    - setQuotePaneData = {useReducer, used to set the quote pane data}

//  Example:
//    <QuoteDetail
//      index={index}
//      quote={item}
//      quotePaneData={quotePaneData}
//      setQuotePaneData={setQuotePaneData}
//    ></QuoteDetail>    

//================================================================


//Libraries
import React, { useState } from 'react';

//Contexts

//Components

//Functions
import ConvertDate from '../../../../../Library/ConvertDate';

//Images
import Expand from '../../../../../Components/Images/Icon_Expand_Teal.svg';
import Collapse from '../../../../../Components/Images/Icon_Collapse_Teal.svg';
import File from '../../../../../Components/Images/Icon_File_Black.svg';
import ButtonDropdown from '../../../../../Components/Images/Icon_ButtonDropdown_White.svg';
import ButtonDropdownDisabled from '../../../../../Components/Images/Icon_ButtonDropdown_Grey.svg';

//CSS


export default function QuoteDetail({
  index,
  quote,
  quotePaneData,
  setQuotePaneData
}) {


  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

    // Used to determine if the section is expanded/collapsed
    const [sectionCollapsed, setSectionCollapsed] = useState(index === 0 ? false : true);

    // Used to store the quote status > 'For Review', 'Completed', 'Rejected', 'Cancelled'
    const [status, setStatus] = useState(quote?.status);

    // Used to toggle visibility of action dropdown menu
    const [dropdownMenuOpen, setDropdownMenuOpen] = useState(false);

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <div className='Task-Section-Container'>
      
        {/* Header */}
        <div className='flex flex-row gap-3 justify-between px-[2%] py-[18px] cursor-pointer w-full' onClick={() => setSectionCollapsed(!sectionCollapsed)}>
            
            <div className='flex flex-row items-center gap-3'>
                <img
                    className='cursor-pointer w-fit'
                    src={sectionCollapsed ? Expand : Collapse}
                    alt={'>'}
                ></img>

                <h4 className='text-[20px] m-0'>Quote</h4>
            </div>

            {/* Status Label */}
            {
                [
                    {
                        'status': 'For Review',
                        'label':'Quote Review',
                        'background': '#FBF6D9',
                        'color': '#DC9A03'
                    },
                    {
                        'status': 'Completed',
                        'label': 'Quote Approved',
                        'background': '#E7F2DA',
                        'color': '#237B4B'
                    },
                    {
                        'status': 'Rejected',
                        'label': 'Quote Rejected',
                        'background': '#FCF4F6',
                        'color': '#C4314B'
                    },
                ]?.filter((style) => style.status === status)
                .map((object, index) => (
                    <label key={index} style={{ backgroundColor: object.background, color: object.color, border: `1px solid ${object.color}` }} className='px-3 py-1 rounded-[30px] font-medium text-[14px]'>
                        {object.label}
                    </label>
                ))
            }

        </div>
        <hr className='m-0' hidden={sectionCollapsed}></hr>
        
        {/* Body */}
        <div className='p-[3%] flex flex-col gap-3' hidden={sectionCollapsed}>

            {/* Info Banner */}
            <div className='text-[#535353] leading-7 py-3 px-4 bg-[#EDF0FA] border-l-[7px] border-l-[#4965CB]'>
                <strong className='capitalize'>{quote?.lastmodifiedby?.givenname} {quote?.lastmodifiedby?.surname}</strong> has provided a quote on {ConvertDate(quote?.lastmodifieddate)}.
                <br></br>
                Comment: {quote?.comment}
            </div>

            {/* Quote Table */}
            <div className='border border-[#D8D8D8] rounded'>
                <table className='w-full max-h-96 overflow-y-scroll'>
                    <thead>
                        <tr className='text-[#424242]'>
                            <th className='py-[15px] px-[20px] font-medium'>Description</th>
                            <th className='py-[15px] px-[10px] font-medium'>Quantity</th>
                            <th className='py-[15px] px-[10px] font-medium'>Unit Price (exTax)</th>
                            <th className='py-[15px] px-[10px] font-medium'>Total (exTax)</th>
                            <th className='py-[15px] px-[10px] font-medium'>Currency</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            quote?.quote?.length === 0 || quote?.quote === undefined ?
                                (
                                    <tr>
                                        <td className='border-t border-t-[#D8D8D8]' colSpan={5}>
                                            <div className='text-center p-[2%]'>
                                                Click on the <b>+</b> to add costs
                                            </div>
                                        </td>
                                    </tr>
                                )
                                :
                                (
                                    quote?.quote?.map((item, index) => (
                                        <tr key={index} className='hover:bg-[#F0F7F7]'>

                                            {/* Description */}
                                            <td className='py-[10px] px-[20px] border-t border-t-[#D8D8D8]'>
                                                <div className='max-w-[250px] leading-normal whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer'>
                                                    {item?.description?.length > 0 ? item?.description : '-'}
                                                </div>
                                            </td>

                                            {/* Quantity */}
                                            <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                {item?.quantity}
                                            </td>

                                            {/* Unit Price (exTax) */}
                                            <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                {item?.unitprice}
                                            </td>

                                            {/* Total (exTax) */}
                                            <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                {item?.total}
                                            </td>

                                            {/* Currency */}
                                            <td className='py-[10px] px-[10px] border-t border-t-[#D8D8D8] align-top'>
                                                {item?.currency}
                                            </td>

                                        </tr>
                                    ))
                                )
                        }
                    </tbody>
                </table>
            </div>

            {/* Footer */}
            <div className='flex flex-col gap-2 justify-end w-full'>
    
                {/* Quote Total */}
                <label className='text-end mb-1'>
                    Total Quote Value (exTax): <b>{(quote.quote.reduce((sum, item) => sum + item.total, 0))?.toLocaleString()} {quote?.quote[0]?.currency}</b>
                </label>

                {/* Attached Files */}
                <div className='flex flex-row flex-wrap gap-2' hidden={quote?.attachedfiles?.length === 0}>
                    {
                        quote?.attachedfiles?.map((file, index) => (
                            <div className='w-fit flex flex-row gap-2 bg-[#F5F5F5] px-3 py-2 rounded-md' key={index}>
                                <img src={File} alt='file'></img>
                                <a className='text-[#0972D3] no-underline font-medium' href={file?.fileUrl} target='_blank' rel='noreferrer'>
                                    {file?.fileName}
                                </a>
                            </div>
                        ))
                    }
                </div>

                {/* Reason for Rejection */}
                <div className='flex flex-col gap-3 mt-2' hidden={status !== 'Rejected'}>
                    <label className='font-medium'>
                        Reason for Rejection: <span className='text-[#C4314B]'>*</span>
                    </label>

                    {
                        quote?.status === status && status === 'Rejected' ?
                        <div>
                            {quote?.rejectionreason}
                        </div>
                        :
                        <textarea
                            className='Input-Field-TextArea'
                            id='feedback'
                            value={quotePaneData?.rejectionReason}
                            onChange={(e) => {
                                setQuotePaneData({ 'rejectionReason': (e.target.value) });
                                if (e.target.value?.length > 0 && status === 'Rejected') setQuotePaneData({ 'saveDisabled': false });
                            }}
                            rows='5'
                            cols='40'
                            placeholder='Enter a reason for rejecting this quote'
                            autoComplete='off'
                            disabled={quote?.status === status && status === 'Rejected'}
                        ></textarea>
                    }
                    
                </div>

                {/* Action Button */}
                <div className='w-full flex flex-row justify-end relative'>
                    <button 
                        className='Primary-Button flex flex-row gap-2 items-center' 
                        onClick={() => setDropdownMenuOpen(!dropdownMenuOpen)} 
                        disabled={quote?.status !== 'For Review'}
                    >
                        Action
                        <img src={quote?.status === status && status === 'Rejected' ? ButtonDropdownDisabled : ButtonDropdown} alt='dropdown'></img>
                    </button>

                    {/* Dropdown Menu */}
                    <div className='flex flex-col z-10 absolute top-[42px] bg-white shadow-lg w-[200px]' hidden={!dropdownMenuOpen}>
                        <button 
                            className='p-2 text-left cursor-pointer hover:bg-[#F5F5F5]' 
                            onClick={() => {
                                setStatus('Completed');
                                setDropdownMenuOpen(false);
                                setQuotePaneData({ 
                                    'saveDisabled': false,
                                    'status': 'Completed',
                                    'currentQuote': quote
                                })
                            }}
                        >
                            Approve
                        </button>

                        <button 
                            className='p-2 text-left cursor-pointer text-[#C4314B] hover:bg-[#F5F5F5]' 
                            onClick={() => {

                                setStatus('Rejected');
                                setDropdownMenuOpen(false);
                                setQuotePaneData({
                                    'status': 'Rejected',
                                    'currentQuote': quote
                                });

                                // Check if the rejection reason provided > activate 'Save' button
                                if (quotePaneData?.rejectionReason?.length === 0) {
                                    setQuotePaneData({ 'saveDisabled': true });

                                } else {
                                    setQuotePaneData({ 'saveDisabled': false });

                                }

                            }}
                        >
                            Reject
                        </button>
                    </div>

                </div>

            </div>

        </div>

    </div>
  )
}
